import { ThemeEvents } from '@theme/events';

const TOAST_VISIBLE_MS = 3000;
const TOAST_ANIM_MS = 300;
const TOAST_SHOW_DELAY_MS = 50;
const DEFAULT_SUCCESS_MESSAGE = 'The Item is Added Successfully';
const TOAST_DEDUPE_WINDOW_MS = 400;

let lastToastAt = 0;

/**
 * Global success toast after a real cart add. Hooks into the theme’s existing AJAX
 * add-to-cart (`product-form.js`, quick-view modal, etc.) via `CartAddEvent` - no
 * duplicate fetch handlers or form bypass.
 *
 * @param {string} message
 */
function showToast(message) {
  document.querySelectorAll('.custom-toast').forEach((el) => el.remove());

  const toast = document.createElement('div');
  toast.className = 'custom-toast';
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.textContent = message;
  document.body.appendChild(toast);

  window.setTimeout(() => {
    toast.classList.add('show');
  }, TOAST_SHOW_DELAY_MS);

  window.setTimeout(() => {
    toast.classList.remove('show');
    window.setTimeout(() => toast.remove(), TOAST_ANIM_MS);
  }, TOAST_VISIBLE_MS);
}

function shouldShowToastNow() {
  const now = Date.now();
  if (now - lastToastAt < TOAST_DEDUPE_WINDOW_MS) return false;
  lastToastAt = now;
  return true;
}

function getSuccessMessage() {
  const g = /** @type {typeof globalThis & { Theme?: { translations?: { item_added_success?: string } } }} */ (
    globalThis
  );
  return g.Theme?.translations?.item_added_success || DEFAULT_SUCCESS_MESSAGE;
}

/**
 * Only successful adds that use `source: 'product-form-component'` (same as the theme’s
 * product forms and quick-view success). Skips `didError`, line-item updates, cart refreshes,
 * quick-order bulk flows, etc. Out-of-stock flows never reach this success path.
 *
 * @param {CustomEvent} event
 */
function onCartAddEvent(event) {
  const detail = /** @type {{ data?: { didError?: boolean; source?: string; itemCount?: number | string } } | undefined} */ (
    event.detail
  );
  const data = detail?.data;
  if (!data || data.didError) return;

  // Show toast only for explicit add-to-cart events.
  if (data.source !== 'product-form-component') return;

  if (!shouldShowToastNow()) return;
  showToast(getSuccessMessage());
}

document.addEventListener(ThemeEvents.cartUpdate, onCartAddEvent, { capture: true });

/**
 * Global fallback: if any code path adds to cart via fetch('/cart/add...') and does not
 * dispatch the theme cart event, still show the success toast.
 */
function patchFetchForCartAdds() {
  const g = /** @type {typeof globalThis & { __cartAddToastFetchPatched?: boolean }} */ (globalThis);
  if (g.__cartAddToastFetchPatched) return;
  g.__cartAddToastFetchPatched = true;

  const originalFetch = globalThis.fetch;
  globalThis.fetch = async (...args) => {
    const response = await originalFetch(...args);

    try {
      const input = args[0];
      const reqUrl =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.href
            : input instanceof Request
              ? input.url
              : '';
      if (!reqUrl) return response;

      const url = new URL(reqUrl, globalThis.location?.origin || undefined);
      const isCartAddRequest = /\/cart\/add(\.js)?$/.test(url.pathname);
      if (!isCartAddRequest || !response.ok) return response;

      if (shouldShowToastNow()) {
        showToast(getSuccessMessage());
      }
    } catch (_) {
      // Never block cart behavior because of toast logic.
    }

    return response;
  };
}

patchFetchForCartAdds();
